import React from 'react';
import { Link } from 'react-router-dom';
import { Header, SEOHead } from '../components';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  DevicePhoneMobileIcon,
  CodeBracketIcon,
  PaintBrushIcon,
  CogIcon,
  StarIcon,
  UserGroupIcon,
  ChartBarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  CheckIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const MobileDevelopmentPage: React.FC = () => {
  // SEO setup
  useSEO(pageConfigs.mobileDevelopment);
  const features = [
    {
      title: "Native & Cross-Platform Development",
      description: "Build high-performance mobile apps for iOS and Android using native technologies or cross-platform frameworks.",
      icon: <CodeBracketIcon className="w-8 h-8" />,
      benefits: ["iOS (Swift/Objective-C)", "Android (Kotlin/Java)", "React Native", "Flutter"]
    },
    {
      title: "UI/UX Design",
      description: "Create intuitive, beautiful, and user-friendly mobile interfaces that drive engagement and conversions.",
      icon: <PaintBrushIcon className="w-8 h-8" />,
      benefits: ["User-centered design", "Prototyping", "Design systems", "Accessibility"]
    },
    {
      title: "Backend Integration",
      description: "Seamlessly integrate your mobile app with robust backend systems and third-party APIs.",
      icon: <CogIcon className="w-8 h-8" />,
      benefits: ["API development", "Database design", "Cloud integration", "Real-time features"]
    },
    {
      title: "App Store Optimization",
      description: "Optimize your app for maximum visibility and downloads in the App Store and Google Play.",
      icon: <StarIcon className="w-8 h-8" />,
      benefits: ["ASO strategy", "Keyword optimization", "Screenshots & videos", "Review management"]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Discovery & Planning",
      description: "We analyze your requirements, target audience, and business goals to create a comprehensive development plan."
    },
    {
      step: "02",
      title: "Design & Prototyping",
      description: "Create wireframes, mockups, and interactive prototypes to visualize the user experience."
    },
    {
      step: "03",
      title: "Development & Testing",
      description: "Build the app using best practices with continuous testing and quality assurance."
    },
    {
      step: "04",
      title: "Launch & Maintenance",
      description: "Deploy to app stores and provide ongoing maintenance, updates, and support."
    }
  ];

  const caseStudy = {
    client: "FinTech Startup",
    industry: "Financial Services",
    challenge: "Needed a secure, user-friendly mobile banking app to compete with established players",
    solution: "Developed a cross-platform mobile app with advanced security features and intuitive UX",
    results: [
      "4.8+ star rating on app stores",
      "1M+ downloads in first year",
      "95% user retention rate",
      "Zero security breaches"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.mobileDevelopment} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-4 py-2 mb-6">
                <DevicePhoneMobileIcon className="w-5 h-5 text-blue-600" />
                <span className="text-blue-700 font-semibold text-sm">Mobile App Specialists</span>
              </div>
              
              <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
                Mobile App 
                <span className="text-blue-600"> Development</span>
              </h1>
              
              <p className="text-lg text-slate-700 mb-8 leading-relaxed">
                Transform your business with custom mobile applications that drive engagement, 
                increase revenue, and provide exceptional user experiences across all devices.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link 
                  to="/quote"
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Start Your Project</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
                <Link 
                  to="/portfolio"
                  className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg border-2 border-slate-200 transition-colors text-center"
                >
                  View Portfolio
                </Link>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">4.8+</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Star Rating</div>
                  <div className="text-xs text-slate-500">Average app rating</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">1M+</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Downloads</div>
                  <div className="text-xs text-slate-500">Across all apps</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">95%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Client Satisfaction</div>
                  <div className="text-xs text-slate-500">Success rate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">50+</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Apps Delivered</div>
                  <div className="text-xs text-slate-500">Successfully launched</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <div className="flex items-center space-x-3 mb-6">
                  <DevicePhoneMobileIcon className="w-6 h-6 text-blue-600" />
                  <h3 className="text-2xl font-display font-bold text-slate-900">Free App Consultation</h3>
                </div>
                <p className="text-slate-600 mb-6">
                  Get expert advice on your mobile app idea and receive a detailed 
                  development roadmap with timeline and cost estimates.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Technical feasibility analysis</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Development roadmap</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Cost & timeline estimates</span>
                  </div>
                </div>
                
                <Link 
                  to="/consultation"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-center block"
                >
                  Schedule Free Consultation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Comprehensive Mobile Development
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From concept to launch, we handle every aspect of mobile app development
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-slate-100">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <div className="text-blue-600">{feature.icon}</div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckIcon className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Our Development Process
            </h2>
            <p className="text-xl text-slate-600">
              Proven methodology for successful mobile app development
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">{step.step}</span>
                </div>
                <h3 className="text-xl font-display font-bold text-slate-900 mb-4">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-2 bg-green-100 rounded-full px-4 py-2 mb-6">
                  <StarIcon className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-semibold text-sm">Success Story</span>
                </div>
                
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6">
                  {caseStudy.client}
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Challenge</h4>
                    <p className="text-slate-600">{caseStudy.challenge}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Solution</h4>
                    <p className="text-slate-600">{caseStudy.solution}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4">Results</h4>
                    <div className="space-y-3">
                      {caseStudy.results.map((result, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <CheckIcon className="w-5 h-5 text-green-500" />
                          <span className="text-slate-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-display font-bold text-slate-900 mb-6">Ready to Build Your App?</h3>
                <p className="text-slate-600 mb-6">
                  Join dozens of businesses that have successfully launched their mobile apps with our expert team.
                </p>
                
                <div className="space-y-4">
                  <Link 
                    to="/quote"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-center block"
                  >
                    Start Your Project
                  </Link>
                  <Link 
                    to="/portfolio"
                    className="w-full bg-white hover:bg-gray-50 text-slate-900 font-semibold py-3 px-6 rounded-lg border-2 border-slate-200 transition-colors text-center block"
                  >
                    View Portfolio
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900">
        <div className="container-custom text-center">
          <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mb-6">
            Ready to Build Your Mobile App?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Let's discuss your mobile app idea and create a development plan that brings your vision to life.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2">
              <PhoneIcon className="w-5 h-5" />
              <span>Call (+91) 9711834001</span>
            </button>
            <button className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2">
              <EnvelopeIcon className="w-5 h-5" />
              <span>Send Email</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MobileDevelopmentPage;
